import { cn } from "@/utils/theme";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import type { Connections } from "@/workflow_api/types/workflow";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { Handle, Position } from "@xyflow/react";
import TooltipWrapper from "../../Tooltip";

interface Props {
  node_id: string;
  connections: Connections;
  isConnecting?: boolean;
  isExecutionView?: boolean;
  onAdd: (event: React.MouseEvent, sourceId: string) => void;
  isSelected?: boolean;
  isDeleting?: boolean;
  isValid?: boolean;
  hasError?: boolean;
}

const getHandleIcon = (type: string, name: string = "") => {
  if (type === "default") {
    return <Icon type="far" name="exclamation" />;
  }
  if (type === "false" && name.toLowerCase() === "rechazado") {
    return <Icon type="far" name="exclamation" />;
  }
  if (type === "expired") {
    return <Icon type="far" name="clock" />;
  }
  if (type === "error") {
    //validar nombre de patita error
    return <Icon type="far" name="exclamation" />;
  }
  return <Icon type="far" name="plus" />;
};

const MultiConnections = ({
  node_id,
  connections,
  isConnecting,
  isExecutionView = false,
  isDeleting,
  isSelected,
  hasError,
  isValid,
  onAdd,
}: Props) => {
  const { isHandleConnected } = useWorkflowStore();
  const { isFlowBlocked } = useUIStore();
  const arr = Object.entries(connections);
  return (
    <>
      {arr
        .sort((a, b) => {
          // if a node in a[0] or b[0] is "default", put it at the end
          if (a[0] === "default") return 1;
          if (b[0] === "default") return -1;
          // if a node in a[0] or b[0] is "true", put it first
          if (a[0] === "true") return -1;
          if (b[0] === "true") return 1;
          // if a node in a[0] or b[0] is "false", put it second
          if (a[0] === "false") return -1;
          if (b[0] === "false") return 1;
          // if a node in a[0] or b[0] is "success", put it first (for doc-generator)
          if (a[0] === "success" || a[0] === "result") return -1;
          if (b[0] === "success" || b[0] === "result") return 1;
          // if a node in a[0] or b[0] is "error", put it second (for doc-generator)
          if (a[0] === "error") return -1;
          if (b[0] === "error") return 1;

          if (a[0] === "expired") return 1;
          if (b[0] === "expired") return -1;
          // otherwise, split by underscore and compare numerically if possible
          const aParts = a[0].split("_");
          const bParts = b[0].split("_");
          if (aParts.length > 1 && bParts.length > 1) {
            const aNum = parseInt(aParts[1], 10);
            const bNum = parseInt(bParts[1], 10);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return aNum - bNum;
            }
          }
          return a[0].localeCompare(b[0]);
        })
        .map((connection, index) => {
          let topPercent = `${(100 / (arr.length + 1)) * (index + 1)}%`;

          if (arr.length === 3 && index === 0) {
            topPercent = "15%";
          }
          if (arr.length === 3 && index === 1) {
            topPercent = "50%";
          }
          if (arr.length === 3 && index === 2) {
            topPercent = "85%";
          }
          const isConnected = isHandleConnected(node_id, connection[0]);
          if (!isConnected && (isExecutionView || isFlowBlocked)) return null;
          return (
            <div
              key={`${node_id}-${connection[0]}`}
              className="bg-transparent"
              style={{
                position: "absolute",
                top: topPercent,
                right: -20,
                transform: "translateY(-50%)",
                width: "20px",
                height: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Handle
                type="source"
                position={Position.Right}
                id={connection[0]}
                className="!rounded-none !border-none !bg-transparent z-40 !left-0 no-drag"
                style={{
                  position: "relative",
                  top: "50%",
                  ...(isConnected
                    ? {
                        transform: "translateY(-50%)",
                        width: "20px",
                        //pointerEvents: "none",
                      }
                    : {
                        transform: "translateY(-50%) translateX(200%)",
                      }),
                }}
              >
                <div
                  className={cn(
                    "w-[3px] h-3.5 bg-on-disabled rounded-r-full absolute pointer-events-none left-px",
                    isSelected && "bg-action-quiet",
                    (isDeleting || hasError) && "bg-danger-quiet",
                    isValid && "bg-success-quiet",
                  )}
                />
                <div
                  className={cn(
                    "h-px border border-neutral absolute top-1.5 -z-10 left-1 pointer-events-none ",
                    isConnected ? "w-[20px]" : "w-[20px]",
                    isSelected && "border-action-quiet",
                    hasError && "border-danger-quiet",
                    isValid && "border-success-quiet",
                  )}
                />
              </Handle>

              {/* Add node button - only show if handle is not connected and flow is not blocked */}
              {!isConnected && !isExecutionView && !isFlowBlocked && (
                <>
                  <div
                    className={cn(
                      "!bg-neutral-object relative size-8 flex items-center justify-center left-9",
                      isConnecting && "opacity-0",
                    )}
                  >
                    <TooltipWrapper title={connection[1].display_name || ""}>
                      <Button
                        variant="quiet"
                        size="xs"
                        aria-label="Add node"
                        className="rounded-md border-[1.5px] size-8 no-drag"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAdd(e, connection[0]);
                        }}
                        style={{
                          position: "relative",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          zIndex: 10,
                        }}
                      >
                        {getHandleIcon(
                          connection[0],
                          connection[1].display_name,
                        )}
                      </Button>
                    </TooltipWrapper>
                  </div>
                </>
              )}
            </div>
          );
        })}
    </>
  );
};

export default MultiConnections;
